import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useIdentify, type AuthTenant } from '@/entities/auth';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api';
import { Button, Form } from '@/shared/ui';

import { AuthTextField } from '../AuthTextField';
import { AUTH_HINT, AUTH_TEXT } from '../texts';
import { AuthNotice } from './AuthNotice';
import { GoogleLoginButton, OrDivider } from './GoogleLoginButton';

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Укажите почту')
    .email('Похоже, здесь опечатка в адресе'),
});

type EmailForm = z.infer<typeof schema>;

/**
 * Первый шаг входа — только почта. `/auth/identify` зовётся по кнопке
 * «Далее», а не на ввод: каждый вызов идёт в лимит попыток.
 */
export function EmailStep({
  defaultEmail,
  onFound,
}: {
  defaultEmail: string;
  onFound: (email: string, tenants: AuthTenant[]) => void;
}) {
  const form = useForm<EmailForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: defaultEmail },
  });
  const identify = useIdentify();
  const [notice, setNotice] = useState<'not_found' | 'rate_limited' | null>(
    null
  );

  const onSubmit = form.handleSubmit(({ email }) => {
    setNotice(null);
    identify.mutate(email, {
      onSuccess: (response) => {
        if (response.status === 'not_found' || response.tenants.length === 0) {
          setNotice('not_found');
          return;
        }
        onFound(email, response.tenants);
      },
      onError: (error) => {
        if (getApiErrorStatus(error) === 429) {
          setNotice('rate_limited');
          return;
        }
        toast.error(getApiErrorMessage(error, 'Не удалось проверить почту'));
      },
    });
  });

  const busy = identify.isPending;

  return (
    <div className="grid gap-4">
      <Form {...form}>
        <form onSubmit={onSubmit} noValidate className="grid gap-4">
          <AuthTextField
            control={form.control}
            name="email"
            label="Почта"
            type="email"
            placeholder="name@company.kz"
            autoComplete="email"
            hint={AUTH_HINT.email}
            autoFocus
            disabled={busy}
          />

          {notice === 'not_found' ? (
            <AuthNotice text={AUTH_TEXT.notFound} hint={AUTH_HINT.notFound} />
          ) : null}
          {notice === 'rate_limited' ? (
            <AuthNotice
              tone="error"
              text={AUTH_TEXT.identifyRateLimited}
              hint={AUTH_HINT.rateLimited}
            />
          ) : null}

          <Button
            type="submit"
            size="lg"
            disabled={busy}
            className="mt-2 w-full rounded-xl"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Проверяем…
              </>
            ) : (
              <>
                Далее
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      </Form>

      <OrDivider />
      <GoogleLoginButton disabled={busy} />
    </div>
  );
}
